/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { parseAnnotationPropertyId } from "#src/annotation/index.js";
import { schemePattern } from "#src/kvstore/url.js";
import { toggleBoolPropertyToolJson } from "#src/layer/annotation/tool_state.js";
import type { UserLayer } from "#src/layer/index.js";
import { layerTypes } from "#src/layer/index.js";
import { StatusMessage } from "#src/status.js";
import {
  ensureUniquePropertyIdentifier,
  sanitizeAnnotationPropertyIdentifier,
} from "#src/ui/annotation_schema_tab.js";
import { bindCommandPalette } from "#src/ui/command_palette.js";
import {
  bindDefaultCopyHandler,
  bindDefaultPasteHandler,
} from "#src/ui/default_clipboard_handling.js";
import { registerDefaultCommands } from "#src/ui/default_commands.js";
import { setDefaultInputEventBindings } from "#src/ui/default_input_event_bindings.js";
import { makeDefaultViewer } from "#src/ui/default_viewer.js";
import type { MinimalViewerOptions } from "#src/ui/minimal_viewer.js";
import { bindScreenshotDialog } from "#src/ui/screenshot_menu.js";
import { bindStateEditor } from "#src/ui/state_editor.js";
import { bindTitle } from "#src/ui/title.js";
import type { Tool } from "#src/ui/tool.js";
import { restoreTool } from "#src/ui/tool.js";
import { UrlHashBinding } from "#src/ui/url_hash_binding.js";
import type {
  ActionIdentifier,
  EventAction,
  EventActionMap,
  EventIdentifier,
} from "#src/util/event_action_map.js";
import {
  verifyObject,
  verifyObjectProperty,
  verifyString,
} from "#src/util/json.js";
import type { Viewer } from "#src/viewer.js";

declare let NEUROGLANCER_DEFAULT_STATE_FRAGMENT: string | undefined;

type CustomToolBinding = {
  layerType: string;
  tool: unknown;
  provider?: string;
};

type CustomBinding = {
  action: EventAction | ActionIdentifier | CustomToolBinding | boolean;
  context?: "global" | "perspectiveView" | "sliceView";
};

type CustomBindings = {
  [identifier: EventIdentifier]: CustomBinding | CustomBinding[];
};

declare const NEUROGLANCER_CUSTOM_INPUT_BINDINGS: CustomBindings | undefined;

export const hasCustomBindings =
  typeof NEUROGLANCER_CUSTOM_INPUT_BINDINGS !== "undefined" &&
  Object.keys(NEUROGLANCER_CUSTOM_INPUT_BINDINGS).length > 0;

function setCustomInputEventBindings(viewer: Viewer, bindings: CustomBindings) {
  const bindNonLayerSpecificTool = (
    key: string,
    customBinding: CustomToolBinding,
  ) => {
    const { layerType, provider: desiredProvider } = customBinding;
    let toolJson = customBinding.tool;
    const desiredLayerConstructor = layerTypes.get(layerType);
    if (desiredLayerConstructor === undefined) {
      throw new Error(`Invalid layer type: ${layerType}`);
    }
    const toolKey = key.charAt(key.length - 1).toUpperCase();
    let previousTool: Tool<object> | undefined;
    let previousLayer: UserLayer | undefined;
    if (typeof toolJson === "string") {
      toolJson = { type: toolJson };
    }
    verifyObject(toolJson);
    const type = verifyObjectProperty(toolJson, "type", verifyString);
    const action = `tool-${type}`;
    viewer.bindAction(action, () => {
      const acceptableLayers = viewer.layerManager.managedLayers.filter(
        (managedLayer) => {
          const correctLayerType =
            managedLayer.layer instanceof desiredLayerConstructor;
          if (desiredProvider && correctLayerType) {
            for (const dataSource of managedLayer.layer?.dataSources || []) {
              const m = dataSource.spec.url.match(schemePattern)!;
              const scheme = m[1];
              if (scheme === desiredProvider) {
                return true;
              }
            }
            return false;
          } else {
            return correctLayerType;
          }
        },
      );
      if (acceptableLayers.length > 0) {
        const firstLayer = acceptableLayers[0].layer;
        if (firstLayer) {
          if (firstLayer !== previousLayer) {
            previousTool = restoreTool(firstLayer, toolJson);
            previousLayer = firstLayer;
          }
          if (previousTool) {
            viewer.activateTool(toolKey, previousTool);
          }
        }
      }
    });
    return action;
  };

  const deleteKey = (map: EventActionMap, key: string) => {
    map.delete(key);
    for (const pMap of map.parents) {
      deleteKey(pMap, key);
    }
  };

  for (const [key, val] of Object.entries(bindings)) {
    const bindings = Array.isArray(val) ? val : [val];
    for (const { action, context = "global" } of bindings) {
      const actionMap = viewer.inputEventBindings[context];
      if (actionMap === undefined) {
        throw new Error(`invalid action map context: ${context}`);
      }
      if (typeof action === "boolean") {
        if (action === false) {
          deleteKey(actionMap, key);
        }
      } else if (typeof action === "string" || "action" in action) {
        actionMap.set(key, action);
      } else {
        const toolAction = bindNonLayerSpecificTool(key, action);
        actionMap.set(key, toolAction);
      }
    }
  }
}

export function convertLegacyAnnotationTags(layer: any) {
  if (layer?.tab === "tags") {
    layer.tab = "schema";
  }
  if (Array.isArray(layer?.panels)) {
    for (const panel of layer.panels) {
      if (panel?.tab === "tags") {
        panel.tab = "schema";
      }
    }
  }
  if (!Array.isArray(layer?.annotationProperties)) return false;
  const properties = layer.annotationProperties;
  const usedIdentifiers = new Set<string>();
  for (const property of properties) {
    if (typeof property?.tag !== "string" && typeof property?.id === "string") {
      usedIdentifiers.add(property.id);
    }
  }
  const convertedIdentifiers = new Map<string, string>();
  let converted = false;
  layer.annotationProperties = properties.map((property: any) => {
    if (typeof property?.tag !== "string") return property;
    converted = true;
    const { tag, enum_labels, enum_values, ...booleanProperty } = property;
    let suggestedIdentifier =
      sanitizeAnnotationPropertyIdentifier(tag) || "tag";
    try {
      parseAnnotationPropertyId(suggestedIdentifier);
    } catch {
      suggestedIdentifier = sanitizeAnnotationPropertyIdentifier(
        `tag_${suggestedIdentifier}`,
      );
    }
    const identifier = ensureUniquePropertyIdentifier(
      suggestedIdentifier,
      usedIdentifiers,
    );
    usedIdentifiers.add(identifier);
    if (typeof property.id === "string") {
      convertedIdentifiers.set(property.id, identifier);
    }
    return { ...booleanProperty, id: identifier, type: "bool" };
  });
  if (typeof layer.shader === "string") {
    for (const [oldIdentifier, newIdentifier] of convertedIdentifiers) {
      const escapedIdentifier = oldIdentifier.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      layer.shader = layer.shader.replace(
        new RegExp(`\\bprop_${escapedIdentifier}(?=\\s*\\()`, "g"),
        `prop_${newIdentifier}`,
      );
    }
  }
  if (layer.toolBindings !== undefined) {
    for (const [key, tool] of Object.entries(layer.toolBindings)) {
      if (typeof tool !== "string" || !tool.startsWith("tagTool_")) continue;
      const identifier = convertedIdentifiers.get(
        tool.slice("tagTool_".length),
      );
      if (identifier !== undefined) {
        layer.toolBindings[key] = toggleBoolPropertyToolJson(identifier);
      }
    }
  }
  return converted;
}

/**
 * Sets up the default neuroglancer viewer.
 */
export function setupDefaultViewer(options?: Partial<MinimalViewerOptions>) {
  const viewer = ((<any>window).viewer = makeDefaultViewer(options));
  setDefaultInputEventBindings(viewer.inputEventBindings);
  if (hasCustomBindings) {
    setCustomInputEventBindings(viewer, NEUROGLANCER_CUSTOM_INPUT_BINDINGS!);
  }

  viewer.stateUpgrader = (state) => {
    // convert graphene state timestamp to layer timestamp
    const fixTimestamp = (layer: any) => {
      if (layer.source?.state?.timestamp) {
        layer.timestamp = layer.source.state.timestamp;
        layer.source.state.timestamp = undefined;
      }
    };
    if (state.layers) {
      const layers = Array.isArray(state.layers)
        ? state.layers
        : Object.values(state.layers);
      layers.map(fixTimestamp);
      let convertedLegacyAnnotationTags = false;
      for (const layer of layers) {
        convertedLegacyAnnotationTags =
          convertLegacyAnnotationTags(layer) || convertedLegacyAnnotationTags;
      }
      if (convertedLegacyAnnotationTags) {
        const status = new StatusMessage();
        status.setErrorMessage(
          "Warning: Local annotations in deprecated format have been safely converted.  Copy this state again to preserve in the new format.",
        );
      }
    }
    return state;
  };

  const hashBinding = viewer.registerDisposer(
    new UrlHashBinding(
      viewer.state,
      viewer.dataSourceProvider.sharedKvStoreContext,
      {
        defaultFragment:
          typeof NEUROGLANCER_DEFAULT_STATE_FRAGMENT !== "undefined"
            ? NEUROGLANCER_DEFAULT_STATE_FRAGMENT
            : undefined,
        upgradeState: viewer.stateUpgrader,
      },
    ),
  );
  viewer.registerDisposer(
    hashBinding.parseError.changed.add(() => {
      const { value } = hashBinding.parseError;
      if (value !== undefined) {
        const status = new StatusMessage();
        status.setErrorMessage(`Error parsing state: ${value.message}`);
        console.log("Error parsing state", value);
      }
      hashBinding.parseError;
    }),
  );
  hashBinding.updateFromUrlHash();
  viewer.registerDisposer(bindTitle(viewer.title));

  bindDefaultCopyHandler(viewer);
  bindDefaultPasteHandler(viewer);
  registerDefaultCommands(viewer.commandRegistry);
  bindCommandPalette(viewer);
  bindStateEditor(viewer);
  bindScreenshotDialog(viewer);

  return viewer;
}
